import type { DisplayMinAgeView, DisplayMinAgeWrite } from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент времени до показа §82 запросного канала §7.6: один путь
 * `/api/display-min-age` для трёх операций. Все три отвечают видом настройки;
 * `400` — негодное значение, `401` — нет сессии, прочее — хранилище.
 */

const DISPLAY_MIN_AGE_PATH = "/api/display-min-age";

function isDisplayMinAgeView(value: unknown): value is DisplayMinAgeView {
  return (
    isRecord(value) &&
    (value.min_age_sec === null ||
      (typeof value.min_age_sec === "number" &&
        Number.isInteger(value.min_age_sec) &&
        value.min_age_sec >= 0))
  );
}

export type DisplayMinAgeFetchResult =
  | { status: "ok"; view: DisplayMinAgeView }
  | { status: "unauthorized" }
  | { status: "unavailable" };

export type DisplayMinAgeWriteResult =
  | { status: "ok"; view: DisplayMinAgeView }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

async function request(
  method: "GET" | "PUT" | "DELETE",
  write?: DisplayMinAgeWrite,
): Promise<DisplayMinAgeWriteResult> {
  let response: Response;
  try {
    response = await fetch(DISPLAY_MIN_AGE_PATH, {
      method,
      headers: write === undefined ? undefined : { "Content-Type": "application/json" },
      body: write === undefined ? undefined : JSON.stringify(write),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (!response.ok) {
    console.error("время до показа отклонено демоном", method, response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    return isDisplayMinAgeView(body) ? { status: "ok", view: body } : { status: "unavailable" };
  } catch {
    return { status: "unavailable" };
  }
}

export async function fetchDisplayMinAge(): Promise<DisplayMinAgeFetchResult> {
  const result = await request("GET");
  // `400` чтению не свойственен: если он пришёл, это сбой сервера, не отказ.
  return result.status === "invalid" ? { status: "unavailable" } : result;
}

/** Задаёт время до показа в секундах. */
export function saveDisplayMinAge(minAgeSec: number): Promise<DisplayMinAgeWriteResult> {
  return request("PUT", { min_age_sec: minAgeSec });
}

/** Сбрасывает настройку в «не задано». */
export function removeDisplayMinAge(): Promise<DisplayMinAgeWriteResult> {
  return request("DELETE");
}
